import type { WorkspaceTabsPosition } from 'src/models/workspaceTabs';

import { useUserInterfaceSettingsStore } from 'src/stores/userInterfaceSettings';
import { useWorkspaceTabsStore } from 'src/stores/workspaceTabs';

import { CommandBus } from 'src/util/command/CommandBus';

export type WorkspaceModeSwitch = {
	applyCreateActionsInSplitView: (createActionsInSplitView: boolean) => void;
	applyRecordsInSplitView: (recordsInSplitView: boolean) => void;
	applyTabsPosition: (workspaceTabsPosition: WorkspaceTabsPosition) => void;
	applyWorkspaceTabsToggle: (workspaceTabsEnabled: boolean) => void;
};

/**
 * Single entry point for every work mode change (tab workspace vs. focus view).
 *
 * The setup wizard and the settings page both write through this switch, so a work mode change
 * always reaches the user preference backend and the layout looks the same after a login on any
 * device. The split view stays device-local on purpose: it depends on the pointer device and the
 * viewport, not on the account.
 *
 * @param commandBus - Bus that carries the preference command. Omit it to write device-local only.
 * @returns The apply functions for the work mode and its side panel options.
 */
export const useWorkspaceModeSwitch = (commandBus?: CommandBus): WorkspaceModeSwitch => {
	const userInterfaceSettingsStore = useUserInterfaceSettingsStore();
	const workspaceTabsStore = useWorkspaceTabsStore();

	const applyWorkspaceTabsToggle = (workspaceTabsEnabled: boolean): void => {
		userInterfaceSettingsStore.setWorkspaceTabsEnabled(workspaceTabsEnabled, commandBus);
		if (workspaceTabsEnabled) {
			userInterfaceSettingsStore.setSplitViewEnabled(true);
			return;
		}
		workspaceTabsStore.resetTabs();
	};

	const applyTabsPosition = (workspaceTabsPosition: WorkspaceTabsPosition): void => {
		userInterfaceSettingsStore.setWorkspaceTabsPosition(workspaceTabsPosition, commandBus);
	};

	const applyCreateActionsInSplitView = (createActionsInSplitView: boolean): void => {
		userInterfaceSettingsStore.setWorkspaceTabsCreateActionsInSplitView(createActionsInSplitView, commandBus);
	};

	const applyRecordsInSplitView = (recordsInSplitView: boolean): void => {
		userInterfaceSettingsStore.setWorkspaceTabsRecordsInSplitView(recordsInSplitView, commandBus);
	};

	return { applyCreateActionsInSplitView, applyRecordsInSplitView, applyTabsPosition, applyWorkspaceTabsToggle };
};
